import { Container } from "pixi.js";
import type { PresentationTaskRunner } from "@slot/presentation";

/**
 * Single-card flip reveal used by CardFlipGamblePresenter.
 *
 * Scale-X flip (no 3D): cardRoot scales from (1,1) → (0,1) → (1,1). At the midpoint the back face is
 * hidden and the chosen face (redFace / blackFace) shown. The player perceives a card turning to
 * reveal the server's pick. UI chrome: the flip rides the chrome runner.
 *
 *   cardRoot
 *     ├── backFace   (shown initially)
 *     ├── redFace    (hidden; shown when flipTo("red"))
 *     └── blackFace  (hidden; shown when flipTo("black"))
 *
 * The presenter awaits flipTo(...) before showing win/lose text.
 */
export class CardFlipView extends Container {
  public readonly cardRoot: Container;
  private readonly backFace: Container;
  private readonly redFace: Container;
  private readonly blackFace: Container;
  private readonly runner: PresentationTaskRunner;
  /** Duration of each half of the flip in seconds (total = 2x this). */
  private readonly flipHalfDurationSeconds: number;
  /** Hold duration on the back face before the flip starts (suspense). */
  private readonly preFlipHoldSeconds: number;
  private readonly baseScale: { x: number; y: number };
  /** Bumped by every reset/flip — an older flip's steps stop applying. */
  private flipGeneration = 0;

  public constructor(input: {
    backFace: Container;
    redFace: Container;
    blackFace: Container;
    runner: PresentationTaskRunner;
    flipHalfDurationSeconds: number;
    preFlipHoldSeconds: number;
  }) {
    super({ label: "Card" });
    this.cardRoot = new Container({ label: "cardRoot" });
    this.addChild(this.cardRoot);
    this.backFace = input.backFace;
    this.redFace = input.redFace;
    this.blackFace = input.blackFace;
    this.cardRoot.addChild(this.backFace, this.redFace, this.blackFace);
    this.runner = input.runner;
    this.flipHalfDurationSeconds = input.flipHalfDurationSeconds;
    this.preFlipHoldSeconds = input.preFlipHoldSeconds;
    this.baseScale = { x: this.cardRoot.scale.x, y: this.cardRoot.scale.y };
    this.setFace("back");
  }

  /** Immediate state: back face visible, scale restored. Called before the flip and on dismiss. */
  public resetToBack(): void {
    this.flipGeneration += 1;
    this.cardRoot.scale.set(this.baseScale.x, this.baseScale.y);
    this.setFace("back");
  }

  /**
   * Plays the flip to reveal the given server-chosen face. Resolves when the second half of the flip
   * completes. Unknown values fall back to the back face (no observable reveal) — the presenter
   * remains responsible for showing win/lose text regardless.
   */
  public async flipTo(serverChosenValue: string | undefined): Promise<void> {
    const generation = ++this.flipGeneration;
    const live = (): boolean => this.flipGeneration === generation && !this.cardRoot.destroyed;
    const faceKey = this.resolveFace(serverChosenValue);
    const half = Math.max(0, this.flipHalfDurationSeconds);
    const preHold = Math.max(0, this.preFlipHoldSeconds);
    const base = this.baseScale;

    if (preHold > 0) await this.runner.sleep(preHold);
    if (!live()) return;
    await this.runner.tweenProgress(half, (k) => live() && this.cardRoot.scale.set(base.x * (1 - k), base.y), undefined, { easing: "linear" });
    if (!live()) return;
    this.setFace(faceKey);
    await this.runner.tweenProgress(half, (k) => live() && this.cardRoot.scale.set(base.x * k, base.y), undefined, { easing: "linear" });
  }

  private resolveFace(value: string | undefined): "red" | "black" | "back" {
    if (value === "red") return "red";
    if (value === "black") return "black";
    return "back";
  }

  private setFace(face: "red" | "black" | "back"): void {
    this.backFace.visible = face === "back";
    this.redFace.visible = face === "red";
    this.blackFace.visible = face === "black";
  }
}
